<template>
  <div class="order-card pa-4">
    <div class="d-flex flex-wrap align-start mb-4">
      <div class="me-4 mb-2">
        <div class="text-h5 font-weight-semibold text--primary">
          Заказ #{{ id }}
        </div>
        <div class="text--secondary">
          {{ orderDate }}<span v-if="restaurant"> · {{ restaurant }}</span>
        </div>
      </div>

      <v-spacer></v-spacer>

      <div class="d-flex flex-wrap align-center order-card__chips">
        <v-chip
          small
          outlined
        >
          {{ sourceText }}
        </v-chip>
        <v-chip
          small
          outlined
        >
          {{ orderTypeText }}
        </v-chip>
        <v-chip
          small
          :color="paymentChip.color"
          text-color="white"
        >
          {{ paymentChip.text }}
        </v-chip>
        <v-chip
          small
          :color="statusChip.color"
          text-color="white"
        >
          {{ statusChip.text }}
        </v-chip>
        <span
          v-if="statusSince"
          class="text-caption text--secondary"
        >с {{ statusSince }}</span>
        <v-chip
          v-if="creationFailed"
          small
          color="error"
          text-color="white"
        >
          нет в Delivery
        </v-chip>
      </div>
    </div>

    <v-row>
      <v-col
        cols="12"
        md="4"
      >
        <v-card
          outlined
          class="fill-height"
        >
          <v-card-title class="text-subtitle-1 font-weight-semibold pb-2">
            Гость
          </v-card-title>
          <v-card-text>
            <table class="order-card__rows">
              <tr>
                <td>Имя</td>
                <td>{{ guestName || 'не указано' }}</td>
              </tr>
              <tr>
                <td>Телефон</td>
                <td>
                  <a
                    v-if="phoneNumber"
                    :href="`tel:${phoneNumber}`"
                  >{{ phoneNumber }}</a>
                  <span v-else>—</span>
                </td>
              </tr>
            </table>
            <div
              v-if="comment"
              class="order-card__note mt-3"
            >
              <div class="text-caption text--secondary">
                Комментарий к заказу
              </div>
              {{ comment }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="4"
      >
        <v-card
          outlined
          class="fill-height"
        >
          <v-card-title class="text-subtitle-1 font-weight-semibold pb-2">
            {{ isDelivery ? 'Доставка' : 'Самовывоз' }}
          </v-card-title>
          <v-card-text>
            <table class="order-card__rows">
              <tr v-if="isDelivery">
                <td>Адрес</td>
                <td>{{ addressLine || 'не указан' }}</td>
              </tr>
              <tr
                v-for="row in addressDetails"
                :key="row.label"
              >
                <td>{{ row.label }}</td>
                <td>{{ row.value }}</td>
              </tr>
              <tr v-if="!isDelivery">
                <td>Откуда</td>
                <td>{{ restaurant || '—' }}</td>
              </tr>
              <tr>
                <td>Когда</td>
                <td>{{ cookAt ? `к ${cookAt}` : 'как можно скорее' }}</td>
              </tr>
            </table>
            <div
              v-if="address && address.comment"
              class="order-card__note mt-3"
            >
              <div class="text-caption text--secondary">
                Комментарий к адресу
              </div>
              {{ address.comment }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="4"
      >
        <v-card
          outlined
          class="fill-height"
        >
          <v-card-title class="text-subtitle-1 font-weight-semibold pb-2">
            Оплата
          </v-card-title>
          <v-card-text>
            <table class="order-card__rows">
              <tr>
                <td>Способ</td>
                <td>{{ paymentMethod || '—' }}</td>
              </tr>
              <tr>
                <td>Сумма заказа</td>
                <td class="font-weight-semibold">
                  {{ money(sum) }}
                </td>
              </tr>
              <tr v-if="changeFrom">
                <td>Сдача с</td>
                <td>{{ money(changeFrom) }} <span class="text--secondary">(сдача {{ money(changeFrom - sum) }})</span></td>
              </tr>
              <tr v-if="isPaid">
                <td>Оплачено</td>
                <td>
                  {{ money(paidAmount) }}
                  <div
                    v-if="paidAt"
                    class="text-caption text--secondary"
                  >
                    {{ paidAt }}
                  </div>
                </td>
              </tr>
              <tr v-if="cashAmount !== null">
                <td>По кассе</td>
                <td :class="{ 'error--text font-weight-semibold': amountMismatch }">
                  {{ money(cashAmount) }}
                </td>
              </tr>
            </table>
            <v-alert
              v-if="amountMismatch"
              type="warning"
              text
              dense
              class="mt-3 mb-0"
            >
              Касса посчитала иначе, чем мы: разница {{ money(cashAmount - sum) }}.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card
      outlined
      class="mt-4"
    >
      <v-card-title class="text-subtitle-1 font-weight-semibold pb-2">
        Delivery и касса
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <table class="order-card__rows">
              <tr>
                <td>Номер в Delivery</td>
                <td>
                  <span v-if="isCreatedInDelivery">{{ deliveryOrderId }}</span>
                  <span
                    v-else-if="awaitsPayment"
                    class="text--secondary"
                  >уйдёт в Delivery после оплаты</span>
                  <span
                    v-else
                    class="error--text font-weight-semibold"
                  >заказ не создан</span>
                </td>
              </tr>
              <tr v-if="isCreatedInDelivery">
                <td>Статус в Delivery сейчас</td>
                <td>
                  <span :class="{ 'text--secondary': !liveStatusKnown }">{{ liveStatusText }}</span>
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        class="ms-1 my-n1"
                        :loading="liveDeliveryStatusLoading"
                        v-bind="attrs"
                        v-on="on"
                        @click="refreshStatus"
                      >
                        <v-icon size="16">
                          {{ icons.mdiRefresh }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>Спросить Delivery ещё раз</span>
                  </v-tooltip>
                </td>
              </tr>
              <tr>
                <td>Ресторан оплаты</td>
                <td>{{ restaurant || '—' }}</td>
              </tr>
              <tr>
                <td>Ресторан доставки</td>
                <td :class="{ 'warning--text font-weight-semibold': restaurantsDiffer }">
                  {{ deliveryRestaurantName || '—' }}
                </td>
              </tr>
              <tr v-if="isDelivery || courierName">
                <td>Курьер</td>
                <td>{{ courierName || '—' }}</td>
              </tr>
              <tr v-if="liveDeliveryStatus && liveDeliveryStatus.isAvailable">
                <td>Оплата по кассе</td>
                <td>{{ cashPaymentText }}</td>
              </tr>
              <tr>
                <td>Номер на кассе</td>
                <td>{{ cashOrderNumber || '—' }}</td>
              </tr>
            </table>
            <v-alert
              v-if="restaurantsDiffer"
              type="warning"
              text
              dense
              class="mt-3 mb-0"
            >
              Оплата ушла в «{{ restaurant }}», а заказ в Delivery создан в «{{ deliveryRestaurantName }}».
            </v-alert>
            <div
              v-if="deliveryError"
              class="order-card__error mt-3"
            >
              {{ deliveryError }}
            </div>
            <div
              v-if="liveDeliveryStatus && !liveDeliveryStatus.isAvailable && isCreatedInDelivery"
              class="text--secondary text-caption mt-2"
            >
              Статус не получен: {{ liveDeliveryStatus.error }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card
      outlined
      class="mt-4"
    >
      <v-card-title class="text-subtitle-1 font-weight-semibold pb-2">
        Состав
      </v-card-title>
      <v-simple-table dense>
        <thead>
          <tr>
            <th>Блюдо</th>
            <th class="text-right">
              Кол-во
            </th>
            <th class="text-right">
              Цена
            </th>
            <th class="text-right">
              Сумма
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(dish, index) in dishes"
            :key="index"
          >
            <td class="py-2">
              <div class="d-flex align-start">
                <v-avatar
                  size="36"
                  rounded
                  :class="['me-3', 'flex-shrink-0', { 'order-card__thumb--empty': !dish.image }]"
                >
                  <v-img
                    v-if="dish.image"
                    :src="dish.image"
                  ></v-img>
                  <v-icon
                    v-else
                    size="18"
                  >
                    {{ icons.mdiSilverwareForkKnife }}
                  </v-icon>
                </v-avatar>
                <div>
                  <span class="font-weight-medium">{{ dish.name }}</span>
                  <v-chip
                    v-if="dish.isGift"
                    x-small
                    color="success"
                    text-color="white"
                    class="ms-2"
                  >
                    подарок
                  </v-chip>
                  <div
                    v-for="(modifier, m) in dish.modifiers || []"
                    :key="m"
                    class="text-caption text--secondary"
                  >
                    + {{ modifier.name }}{{ modifier.amount > 1 ? ` ×${modifier.amount}` : '' }}{{ modifier.price ? ` · ${money(modifier.price)}` : '' }}
                    <div
                      v-for="(part, p) in modifier.combos || []"
                      :key="p"
                      class="ms-4"
                    >
                      — {{ part.name }}{{ part.amount > 1 ? ` ×${part.amount}` : '' }}{{ part.price ? ` · ${money(part.price)}` : '' }}
                      <span
                        v-for="(partModifier, pm) in part.modifiers || []"
                        :key="pm"
                      >, {{ partModifier.name }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </td>
            <td class="text-right">
              {{ dish.amount }}
            </td>
            <td class="text-right text-no-wrap">
              {{ dish.isGift ? 'бесплатно' : money(unitPrice(dish)) }}
            </td>
            <td class="text-right text-no-wrap">
              {{ dish.isGift ? '—' : money(unitPrice(dish) * dish.amount) }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr v-if="linesDiffer">
            <td class="text--secondary">
              По строкам меню
            </td>
            <td colspan="2"></td>
            <td class="text-right text--secondary text-no-wrap">
              {{ money(linesTotal) }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-semibold">
              Сумма заказа
              <div
                v-if="linesDiffer"
                class="text-caption text--secondary font-weight-regular"
              >
                Отличается от строк меню: скидки, доставка или пересчёт кассой
              </div>
            </td>
            <td colspan="2"></td>
            <td class="text-right font-weight-semibold text-no-wrap">
              {{ money(sum) }}
            </td>
          </tr>
        </tfoot>
      </v-simple-table>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  mdiRefresh, mdiSilverwareForkKnife,
} from '@mdi/js'
import { money } from '@/helpers'
import { orderStatusColor, toCardDateTime } from '@/helpers/orders'

const CASH_PAYMENT = {
  notPaid: 'не оплачен',
  prepaid: 'предоплачен',
  paid: 'оплачен на кассе',
}

export default {
  name: 'OrderForm',
  data: () => ({
    icons: {
      mdiRefresh, mdiSilverwareForkKnife,
    },
  }),
  computed: {
    ...mapState('order/form', [
      'id', 'orderDate', 'source', 'orderType', 'restaurant',
      'guestName', 'phoneNumber', 'comment', 'cookAt', 'address',
      'paymentMethod', 'paymentType', 'sum', 'changeFrom', 'isPaid', 'paidAmount', 'paidAt',
      'deliveryOrderId', 'isCreatedInDelivery', 'deliveryError',
      'deliveryRestaurantName', 'courierName',
      'deliveryStatus', 'deliveryStatusName', 'statusChangedAt', 'state', 'stateName',
      'dishes', 'liveDeliveryStatus', 'liveDeliveryStatusLoading',
    ]),
    isDelivery() {
      return this.orderType === 'Delivery'
    },
    isOnline() {
      return this.paymentType === 'Online'
    },
    sourceText() {
      return this.source === 'MobileApp' ? 'Приложение' : 'Сайт'
    },
    orderTypeText() {
      return this.isDelivery ? 'Доставка' : 'Самовывоз'
    },

    paymentChip() {
      if (this.isPaid) return { text: 'оплачен', color: 'success' }
      if (this.isOnline) return { text: 'не оплачен', color: 'error' }

      return { text: 'оплата при получении', color: 'grey' }
    },

    awaitsPayment() {
      return this.isOnline && !this.isPaid && !this.isCreatedInDelivery && !this.deliveryError
    },

    creationFailed() {
      return Boolean(this.id) && !this.isCreatedInDelivery && !this.awaitsPayment
    },

    statusChip() {
      if (this.deliveryStatusName) {
        return { text: this.deliveryStatusName, color: orderStatusColor(this.deliveryStatus) }
      }

      return {
        text: this.stateName || '—',
        color: this.state === 'WaitingForPayment' ? 'warning' : 'secondary',
      }
    },

    statusSince() {
      return toCardDateTime(this.statusChangedAt)
    },

    liveStatusKnown() {
      return Boolean(this.liveDeliveryStatus && this.liveDeliveryStatus.isAvailable)
    },
    liveStatusText() {
      if (this.liveStatusKnown) return this.liveDeliveryStatus.statusName || '—'
      if (this.liveDeliveryStatusLoading) return 'спрашиваем Delivery…'

      return this.liveDeliveryStatus ? 'не получен' : '—'
    },
    addressLine() {
      if (!this.address) return ''

      const address = this.address
      const houseLine = [address.house, address.building].filter(Boolean).join('/')

      return [address.city, address.street, houseLine].filter(Boolean).join(', ')
    },
    addressDetails() {
      if (!this.address) return []

      return [
        { label: 'Подъезд', value: this.address.entry },
        { label: 'Этаж', value: this.address.floor },
        { label: 'Квартира', value: this.address.apartments },
        { label: 'Домофон', value: this.address.entryCode },
      ].filter(row => row.value)
    },
    cashAmount() {
      const amount = this.liveDeliveryStatus && this.liveDeliveryStatus.isAvailable
        ? this.liveDeliveryStatus.fullAmount
        : null

      return amount === undefined ? null : amount
    },

    amountMismatch() {
      return this.cashAmount !== null && Math.abs(this.cashAmount - this.sum) >= 1
    },
    cashPaymentText() {
      const status = this.liveDeliveryStatus && this.liveDeliveryStatus.paymentStatus

      return CASH_PAYMENT[status] || status || '—'
    },
    cashOrderNumber() {
      return this.liveDeliveryStatus && this.liveDeliveryStatus.cashOrderId
    },

    restaurantsDiffer() {
      return Boolean(this.restaurant && this.deliveryRestaurantName
        && this.restaurant.trim() !== this.deliveryRestaurantName.trim())
    },
    linesTotal() {
      return this.dishes
        .filter(dish => !dish.isGift)
        .reduce((total, dish) => total + this.unitPrice(dish) * dish.amount, 0)
    },

    linesDiffer() {
      return Math.abs(this.linesTotal - this.sum) >= 1
    },
  },
  async created() {
    const { id } = this.$route.params

    await this.fetchById(id)

    if (this.isCreatedInDelivery) this.fetchLiveDeliveryStatus(id)
  },
  methods: {
    ...mapActions('order/form', ['fetchById', 'fetchLiveDeliveryStatus']),
    money,

    unitPrice(dish) {
      const modifiers = (dish.modifiers || []).reduce((total, modifier) => {
        const parts = (modifier.combos || [])
          .reduce((sum, part) => sum + (part.price || 0) * (part.amount || 1), 0)

        return total + (modifier.price || 0) * (modifier.amount || 1) + parts
      }, 0)

      return (dish.price || 0) + modifiers
    },
    refreshStatus() {
      this.fetchLiveDeliveryStatus(this.id)
    },
  },
}
</script>

<style lang="scss" scoped>
.order-card__chips {
  gap: 6px;
}

.order-card__rows {
  border-collapse: collapse;
  width: 100%;

  td {
    padding: 3px 0;
    vertical-align: top;
  }

  td:first-child {
    color: rgba(94, 86, 105, 0.68);
    padding-right: 16px;
    white-space: nowrap;
    width: 1%;
  }
}

.order-card__thumb--empty {
  background: rgba(94, 86, 105, 0.08);
}

.order-card__note {
  background: rgba(145, 85, 253, 0.06);
  border-radius: 6px;
  padding: 8px 10px;
  white-space: pre-line;
}

.order-card__error {
  white-space: pre-line;
  color: var(--v-error-base);
  font-size: 0.8125rem;
}
</style>
